import React, { useCallback } from "react";
import { Box } from "arkh";
import { DateTime } from "luxon";
import { graphql, useLazyLoadQuery } from "react-relay";
import { KPISelectorQuery } from "./__generated__/KPISelectorQuery.graphql";
import { KPISelectorList } from "./KPISelectorList";
import { useTimeZone } from "../../OS/Timezone";

KPISelector.height = 60;

export function KPISelector(props: {
  currentSequenceNumber: number;
  routineId: string;
  onSelectOccasion: (sequenceNumber: number) => void;
  fetchKey: string;
}) {
  const data = useLazyLoadQuery<KPISelectorQuery>(
    graphql`
      query KPISelectorQuery($routineId: ID!, $after: String, $first: Int) {
        routine(id: $routineId) {
          id
          title
          schedule {
            rules {
              freq
            }
          }
          ...KPISelectorList_Routine
        }
      }
    `,
    {
      first: 10,
      routineId: props.routineId,
    },
    {
      fetchPolicy: "store-and-network",
      fetchKey: props.fetchKey,
    }
  );

  const freq = data?.routine?.schedule?.rules[0]?.freq ?? "DAILY";

  const tz = useTimeZone();
  const formatDate = useCallback(
    (_date: string): [string, string] => {
      const date = DateTime.fromISO(_date, { zone: tz });

      switch (freq) {
        case "DAILY":
          return [
            date.toLocaleString({
              weekday: "long",
              timeZone: tz,
            }),
            date.toLocaleString({
              month: "long",
              day: "numeric",
              year: undefined,
              timeZone: tz,
            }),
          ];
        case "WEEKLY":
          return [
            date.toFormat("v.W"),
            date.toLocaleString({
              year: "numeric",
              timeZone: tz,
            }),
          ];
        case "MONTHLY":
          return [
            date.toLocaleString({ month: "short", timeZone: tz }),
            date.toLocaleString({
              year: "numeric",
              timeZone: tz,
            }),
          ];
      }
      return ["", ""];
    },
    [freq, tz]
  );
  return (
    <Box
      style={{
        height: KPISelector.height,
        flexDirection: "row",
        alignItems: "center",
      }}
      color="surface-primary"
    >
      {data.routine ? (
        <KPISelectorList
          currentSequenceNumber={props.currentSequenceNumber}
          onSelectOccasion={props.onSelectOccasion}
          formatDate={formatDate}
          routine={data.routine}
        />
      ) : null}
    </Box>
  );
}
